import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import pageTranslatorService from '../services/pageTranslatorService';
import backgroundTranslatorService from '../services/backgroundTranslatorService';

function RouteTranslationHandler() {
  const location = useLocation();
  const { language, setIsPageTranslating } = useLanguage();
  const previousRouteRef = useRef(null);
  const previousLanguageRef = useRef(language);
  const translationTimeoutRef = useRef(null);
  const isInitialMountRef = useRef(true);

  useEffect(() => {
    const currentRoute = location.pathname;
    const currentSearch = location.search;
    const fullRoute = currentRoute + currentSearch;
    const routeChanged = previousRouteRef.current !== fullRoute;
    const languageChanged = previousLanguageRef.current !== language;
    
    const previousLanguage = previousLanguageRef.current;

    previousRouteRef.current = fullRoute;
    previousLanguageRef.current = language;

    if (language === 'en') {
      if (routeChanged || languageChanged) {
        pageTranslatorService.clearCache();
        if (languageChanged) {
          window.location.reload();
        }
        setIsPageTranslating(false);
      }
      return;
    }

    if (translationTimeoutRef.current) {
      clearTimeout(translationTimeoutRef.current);
    }

    if (languageChanged) {
      console.log(`[Route Translation] Language changed - Clearing cache and starting background translation`);
      pageTranslatorService.clearCache();
      backgroundTranslatorService.clearCache();
      
      // Start background translation of other pages
      // This will prepare translations for when user navigates
      setTimeout(() => {
        backgroundTranslatorService.startBackgroundTranslation(language, previousLanguage);
      }, 1000); // Wait 1 second after current page translation starts
    }

    const hasSubParams = currentSearch.includes('sub=') || currentSearch.includes('subsub=');
    const isSubcategoryPage = hasSubParams || currentRoute.includes('/category/');
    const isSubcategoryProductPage = hasSubParams;
    const isPolicyPage = currentRoute.includes('-policy') || 
                         currentRoute.includes('/privacy') ||
                         currentRoute.includes('/terms');
    const isProductDetailPage = currentRoute.startsWith('/product/');
    const isProductsPage = currentRoute === '/products';

    // Optimize wait times for instant translation
    let waitTime;
    if (isPolicyPage) {
      waitTime = languageChanged ? 100 : 200; // Very fast for policy pages
    } else if (isProductDetailPage) {
      // Product detail pages - translate quickly after content loads
      waitTime = languageChanged ? 300 : 500; // Fast for product pages
    } else if (isProductsPage) {
      // Products listing page
      waitTime = languageChanged ? 300 : 600;
    } else if (isSubcategoryPage) {
      if (isSubcategoryProductPage) {
        waitTime = languageChanged ? 2000 : 3000; // Still need time for dynamic content
      } else {
        waitTime = languageChanged ? 1500 : 3000;
      }
    } else {
      // Regular pages (about, contact, etc.) - translate instantly
      waitTime = languageChanged ? 200 : 400;
    }

    if (isInitialMountRef.current) {
      console.log(`[Route Translation] Initial load - Route: ${fullRoute}, Language: ${language}`);
      isInitialMountRef.current = false;
    } else if (routeChanged) {
      console.log(`[Route Translation] Route changed - To: ${fullRoute}, Language: ${language}`);
    } else if (languageChanged) {
      console.log(`[Route Translation] Language changed - From: ${previousLanguage} To: ${language}`);
    }

    // Show loading overlay immediately
    setIsPageTranslating(true);
    
    // Start translation immediately (don't wait for timeout on route change)
    const startTranslation = () => {
      console.log(`[Route Translation] Translating page: ${fullRoute} to ${language}`);
      
      const translatePage = async (retryCount = 0) => {
        try {
          let sourceLang = 'en';
          
          // Check if this page was queued for background translation
          const translationParams = backgroundTranslatorService.getTranslationParams(currentRoute, language);
          
          if (languageChanged && retryCount === 0) {
            pageTranslatorService.clearCache();
            if (previousLanguage && previousLanguage !== 'en' && previousLanguage !== language) {
              sourceLang = previousLanguage;
              console.log(`[Route Translation] Using previous language '${previousLanguage}' as source`);
            } else {
              sourceLang = 'auto';
              console.log(`[Route Translation] Using auto-detect for source language`);
            }
          } else if (translationParams.needsTranslation && translationParams.sourceLanguage) {
            // Use source language from background translation queue
            sourceLang = translationParams.sourceLanguage === 'en' ? 'auto' : translationParams.sourceLanguage;
            console.log(`[Route Translation] Using background translation params, source: ${sourceLang}`);
          }
          
          const translationResult = await pageTranslatorService.translatePage(language, sourceLang);
          
          // Mark this page as translated
          backgroundTranslatorService.markPageAsTranslated(fullRoute, language);
          
          setIsPageTranslating(false);
          console.log(`[Route Translation] ✅ Page translated successfully`);
        } catch (error) {
          console.error(`[Route Translation] ❌ Error:`, error);
          
          if (isSubcategoryPage && retryCount < 2) {
            const retryDelay = isSubcategoryProductPage ? 2000 : 1500;
            console.log(`[Route Translation] Retrying (attempt ${retryCount + 1}/2)...`);
            setTimeout(() => translatePage(retryCount + 1), retryDelay);
          } else {
            setIsPageTranslating(false);
          }
        }
      };

      translatePage();
    };

    // For route changes (not language changes), start translation immediately
    // For language changes, use the wait time to let content load
    if (routeChanged && !languageChanged) {
      // Route changed - translate immediately
      startTranslation();
    } else {
      // Language changed or initial load - wait for content
      translationTimeoutRef.current = setTimeout(() => {
        startTranslation();
      }, waitTime);
    }

    return () => {
      if (translationTimeoutRef.current) {
        clearTimeout(translationTimeoutRef.current);
      }
    };
  }, [location.pathname, location.search, language, setIsPageTranslating]);

  return null;
}

export default RouteTranslationHandler;

